import { Projet } from "../launchCampagn/projet.type";
import { User } from "../user/user.type";

export type RetraitStatut =
  | "initiee"
  | "demande"
  | "approuve"
  | "en_cours"
  | "verse"
  | "echoue"
  | "refuse";

export type RetraitMode = "mobile_money" | "bancaire" | "carte";

export type Retrait = {
  id: number;
  slug: string;
  projet: Projet | number;
  projet_nom: string | null;
  demande_par_nom: string | null;
  traite_par_nom: string | null;
  montant: string;
  statut: RetraitStatut;
  demande_par: User | number | null;
  traite_par: User | number | null;
  date_traitement: string | null;
  motif: string | null;
  mode: RetraitMode;
  pays: string | null;
  canal: string | null;
  numero: string | null;
  banque_titulaire: string | null;
  banque_nom: string | null;
  banque_iban: string | null;
  banque_swift: string | null;
  // Virement par carte : le numero complet ne transite jamais par cette
  // route, seulement ses quatre derniers chiffres.
  carte_derniers: string | null;
  carte_masque: string | null;
  commission: string;
  montant_net: string;
  disburse_token: string | null;
  transaction_id: string | null;
  disburse_tx_id: string | null;
  frais_paydunya: string;
  created_at: string;
};

export type RetraitFormData = {
  montant: string | number;
  projet: number;
  mode?: RetraitMode;
  pays?: string;
  canal?: string;
  numero?: string;
  banque_titulaire?: string;
  banque_nom?: string;
  banque_iban?: string;
  banque_swift?: string;
  carte_numero?: string;
};

export const LIBELLE_MODE: Record<RetraitMode, string> = {
  mobile_money: "Mobile money",
  bancaire: "Virement bancaire",
  carte: "Virement par carte",
};

// Ou part l'argent. Un seul endroit pour les trois moyens : l'historique du
// porteur, sa modale et l'ecran admin affichent tous la meme chose.
export const destinationRetrait = (r: Retrait) => {
  if (r.mode === "carte")
    return (
      [r.banque_nom, r.carte_masque, r.banque_titulaire]
        .filter(Boolean)
        .join(" · ") || LIBELLE_MODE.carte
    );
  if (r.mode === "bancaire")
    return (
      [r.banque_nom, r.banque_iban, r.banque_titulaire]
        .filter(Boolean)
        .join(" · ") || LIBELLE_MODE.bancaire
    );
  return [r.canal, r.numero].filter(Boolean).join(" · ") || "—";
};
// Canaux de VERSEMENT : ils different de ceux de l'encaissement. Expresso est
// "expresso-sn" a l'encaissement et "expresso-senegal" au versement ; Wizall
// et Moov Mali ne sont pas versables. Doit rester aligne sur
// api/paydunya_disburse.py, qui seul fait foi.
export const CANAUX_VERSEMENT: Record<
  string,
  { code: string; nom: string; canaux: { slug: string; label: string }[] }
> = {
  SN: {
    code: "SN",
    nom: "Sénégal",
    canaux: [
      { slug: "orange-money-senegal", label: "Orange Money" },
      { slug: "free-money-senegal", label: "Free Money" },
      { slug: "expresso-senegal", label: "E-Money Expresso" },
      { slug: "wave-senegal", label: "Wave" },
      { slug: "djamo-sn", label: "Djamo" },
    ],
  },
  CI: {
    code: "CI",
    nom: "Côte d'Ivoire",
    canaux: [
      { slug: "orange-money-ci", label: "Orange Money" },
      { slug: "mtn-ci", label: "MTN MoMo" },
      { slug: "moov-ci", label: "Moov Money" },
      { slug: "wave-ci", label: "Wave" },
      { slug: "djamo-ci", label: "Djamo" },
    ],
  },
  BJ: {
    code: "BJ",
    nom: "Bénin",
    canaux: [
      { slug: "mtn-benin", label: "MTN MoMo" },
      { slug: "moov-benin", label: "Moov Money" },
      { slug: "celtiis-cash", label: "Celtiis Cash" },
    ],
  },
  TG: {
    code: "TG",
    nom: "Togo",
    canaux: [
      { slug: "t-money-togo", label: "T-Money" },
      { slug: "moov-togo", label: "Moov Money" },
    ],
  },
  ML: {
    code: "ML",
    nom: "Mali",
    canaux: [{ slug: "orange-money-mali", label: "Orange Money" }],
  },
  BF: {
    code: "BF",
    nom: "Burkina Faso",
    canaux: [
      { slug: "orange-money-burkina", label: "Orange Money" },
      { slug: "moov-burkina-faso", label: "Moov Money" },
    ],
  },
  CM: {
    code: "CM",
    nom: "Cameroun",
    canaux: [{ slug: "mtn-cameroun", label: "MTN MoMo" }],
  },
};

// Le push n'est pas encore ouvert au Cameroun pour ce compte marchand : ces
// retraits, comme les virements bancaires, restent versés à la main.
export const PAYS_SANS_PUSH = ["CM"];

export const pushDisponible = (mode: RetraitMode, pays?: string) =>
  mode === "mobile_money" && !!pays && !PAYS_SANS_PUSH.includes(pays);

export type RetraitStatutFormData = {
  slug: string;
  statut: RetraitStatut;
  motif?: string;
};

// Libelle et couleur de chaque statut, pour l'affichage en tableau.
export const RETRAIT_STATUT: Record<
  RetraitStatut,
  { label: string; color: string }
> = {
  // Creee mais jamais confirmee par le code : visible du seul porteur.
  initiee: { label: "Non confirmée", color: "#8e8ea1" },
  demande: { label: "En attente", color: "#f39c12" },
  approuve: { label: "Approuvée", color: "#2980b9" },
  en_cours: { label: "Versement en cours", color: "#16a085" },
  verse: { label: "Versée", color: "#0FBF56" },
  echoue: { label: "Échouée", color: "#d35400" },
  refuse: { label: "Refusée", color: "#c0392b" },
};

// Un versement passe par l'API PUSH de Paydunya, ou bien l'admin paie
// lui-meme et met le statut a jour. Les references Paydunya ne sont
// renseignees que dans le premier cas : c'est ce qui permet de distinguer.
export const origineVersement = (r: Retrait) => {
  const refPaydunya = r.transaction_id || r.disburse_tx_id || r.disburse_token;
  if (refPaydunya) {
    return { auto: true, label: "PayDunya", reference: refPaydunya };
  }
  if (r.statut === "verse") {
    return {
      auto: false,
      label: "Manuel",
      reference: r.traite_par_nom || "administrateur",
    };
  }
  return null;
};

// Transitions proposees a l'admin. Doit rester aligne sur RETRAIT_TRANSITIONS
// cote backend (api/views.py), qui reste seul juge.
// Sur un pays servi par le push, l'avancement du versement vient de Paydunya :
// le porteur declenche en confirmant son code, puis check-status fait foi.
// L'admin ne doit donc pas disposer de boutons qui feraient croire qu'il
// declenche ou constate un paiement — il ne lui reste que le refus.
export const transitionsAdmin = (r: Retrait): RetraitStatut[] => {
  const toutes = RETRAIT_TRANSITIONS[r.statut] || [];
  if (pushDisponible(r.mode, r.pays || undefined)) {
    return toutes.filter((s) => s === "refuse");
  }
  return toutes;
};

export const RETRAIT_TRANSITIONS: Record<RetraitStatut, RetraitStatut[]> = {
  initiee: [],
  demande: ["approuve", "refuse"],
  approuve: ["en_cours", "refuse"],
  en_cours: ["verse", "echoue"],
  echoue: ["en_cours", "refuse"],
  verse: [],
  refuse: [],
};
